import { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { Clock, User, MapPin } from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

const localizer = momentLocalizer(moment);

export default function ScheduleCalendar() {
  const [jobs, setJobs] = useState([]);
  const [visits, setVisits] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const [jobsRes, visitsRes] = await Promise.all([
        axiosInstance.get('/jobs'),
        axiosInstance.get('/visit-schedules')
      ]);
      
      setJobs(jobsRes.data);
      setVisits(visitsRes.data);
      
      // Convert jobs to calendar events
      const jobEvents = jobsRes.data
        .filter(job => job.scheduled_date)
        .map(job => ({
          id: job.id,
          title: `JOB: ${job.service_name} - ${job.customer_name}`,
          start: new Date(job.scheduled_date),
          end: new Date(new Date(job.scheduled_date).getTime() + job.estimated_hours * 60 * 60 * 1000),
          resource: { ...job, type: 'job' }
        }));
      
      // Convert visits to calendar events
      const visitEvents = visitsRes.data
        .filter(visit => visit.scheduled_date)
        .map(visit => ({
          id: visit.id,
          title: `VISIT: ${visit.visit_purpose} - ${visit.customer_name}`,
          start: new Date(visit.scheduled_date),
          end: new Date(new Date(visit.scheduled_date).getTime() + (visit.estimated_duration || 1) * 60 * 60 * 1000),
          resource: { ...visit, type: 'visit' }
        }));
      
      setEvents([...jobEvents, ...visitEvents]);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
  };

  const eventStyleGetter = (event) => {
    const resource = event.resource;
    let backgroundColor = '#3174ad';
    
    // Different colors for visits vs jobs
    if (resource.type === 'visit') {
      // Blue shades for visits based on status
      switch(resource.status) {
        case 'scheduled':
          backgroundColor = '#3B82F6'; // Blue
          break;
        case 'started':
          backgroundColor = '#2563EB'; // Darker Blue
          break;
        case 'completed':
          backgroundColor = '#10B981'; // Green
          break;
        case 'cancelled':
          backgroundColor = '#EF4444'; // Red
          break;
        default:
          backgroundColor = '#60A5FA'; // Light Blue
      }
    } else {
      // Original job colors
      switch(resource.status) {
        case 'pending':
          backgroundColor = '#FFA500';
          break;
        case 'assigned':
          backgroundColor = '#9333EA';
          break;
        case 'on_the_way':
          backgroundColor = '#6366F1';
          break;
        case 'in_progress':
          backgroundColor = '#F97316';
          break;
        case 'completed':
          backgroundColor = '#10B981';
          break;
        default:
          backgroundColor = '#6B7280';
      }
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      assigned: 'bg-purple-500',
      on_the_way: 'bg-indigo-500',
      in_progress: 'bg-orange-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <div data-testid="schedule-calendar">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Schedule Calendar
        </h2>
        <p className="text-gray-600">View all scheduled visits and jobs with handyman assignments</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
        </div>
      ) : (
        <>
          <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0 mb-6">
            <CardHeader>
              <CardTitle>Legend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Job Status:</p>
                  <div className="flex flex-wrap gap-3">
                    <Badge className="bg-yellow-500 text-white">Pending</Badge>
                    <Badge className="bg-purple-500 text-white">Assigned</Badge>
                    <Badge className="bg-indigo-500 text-white">On The Way</Badge>
                    <Badge className="bg-orange-500 text-white">In Progress</Badge>
                    <Badge className="bg-green-500 text-white">Completed</Badge>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Visit Status:</p>
                  <div className="flex flex-wrap gap-3">
                    <Badge className="bg-blue-500 text-white">Scheduled</Badge>
                    <Badge className="bg-blue-600 text-white">Started</Badge>
                    <Badge className="bg-green-500 text-white">Completed</Badge>
                    <Badge className="bg-red-500 text-white">Cancelled</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
            <CardContent className="p-6">
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 700 }}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
                views={['month', 'week', 'day', 'agenda']}
                defaultView="month"
                popup
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Schedule Details Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedEvent?.type === 'visit' ? 'Visit Details' : 'Job Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold">
                  {selectedEvent.type === 'visit' ? selectedEvent.visit_purpose : selectedEvent.service_name}
                </h3>
                <Badge className={`${getStatusColor(selectedEvent.status)} text-white mt-2`}>
                  {selectedEvent.status.replace('_', ' ').toUpperCase()}
                </Badge>
                {selectedEvent.type === 'visit' && (
                  <Badge className="ml-2 bg-blue-500 text-white">VISIT</Badge>
                )}
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User size={16} className="text-[#595959]" />
                  <span>Customer: {selectedEvent.customer_name}</span>
                </div>
                {selectedEvent.customer_phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-gray-500">Phone:</span>
                    <span>{selectedEvent.customer_phone}</span>
                  </div>
                )}
                {selectedEvent.handyman_name && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User size={16} className="text-purple-600" />
                    <span>Technician: {selectedEvent.handyman_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin size={16} className="text-[#595959]" />
                  <span>{selectedEvent.customer_address || selectedEvent.address}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock size={16} className="text-[#595959]" />
                  <span>
                    {selectedEvent.type === 'visit' 
                      ? `${selectedEvent.estimated_duration || 1}h estimated` 
                      : `${selectedEvent.estimated_hours}h estimated`}
                  </span>
                </div>
                {selectedEvent.scheduled_date && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-gray-500">Scheduled:</span>
                    <span>{formatDateTime(selectedEvent.scheduled_date)}</span>
                  </div>
                )}
              </div>

              {selectedEvent.notes && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500">Notes: {selectedEvent.notes}</p>
                </div>
              )}

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setSelectedEvent(null)}
                data-testid="close-schedule-details"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
